import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AnnouncementPanel,
  InputChips,
  Modal,
  Typography,
} from '@innovamat/glimmer-components';
import { useSchoolStudentsQuery } from '@innovamat/glow-api-client';
import { useDebounce } from '@innovamat/hooks';
import { useUser } from '../../../user-management';
import type { EnrollmentBulkImportFailure } from '../add-classroom-modal/hooks/use-students-enrollment';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StudentsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ErrorText = styled(Typography.Body2)`
  color: ${({ theme }) => theme.tokens.color.alias.cm.text['text-error'].value};
`;

type Props = {
  show: boolean;
  isLoading?: boolean;
  failures?: EnrollmentBulkImportFailure[];
  onSave: (emails: string[]) => void;
  onClose: () => void;
};

function AddStudentsToClassroomModal({
  show,
  onClose,
  onSave,
  isLoading,
  failures,
}: Props): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useUser();
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 400);

  const { data: students, isLoading: isLoadingStudents } =
    useSchoolStudentsQuery({
      body: {
        organizationId: user?.organizationId || '',
        page: 1,
        pageSize: 20,
        name: debouncedSearch,
        hasEmail: true,
      },
    });

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleIsChipInvalid = (chip: string): boolean => {
    return !isValidEmail(chip) || failedEmails?.includes(chip) || false;
  };

  const handleUpdateMails = (emails: string[]): void => {
    setSelectedStudents(emails);
  };

  const handleSave = (): void => {
    const validEmails = selectedStudents.filter((email) => isValidEmail(email));
    if (validEmails.length > 0) {
      onSave(validEmails);
    }
  };

  const isFormValid =
    selectedStudents.length > 0 &&
    selectedStudents.every((email) => isValidEmail(email));

  const options =
    students?.schoolStudents?.map((student) => {
      return {
        id: student?.id || '',
        title: student?.firstName || '',
        subtitle: student?.email || '',
        image: '',
      };
    }) || [];

  const failedEmails = failures?.map((failure) => failure.student);

  useEffect(() => {
    if (failedEmails) {
      setSelectedStudents(failedEmails);
    }
  }, [failures]);

  if (!show) return null;

  return (
    <Modal
      modalWidth={700}
      buttons={[
        {
          children: t('common.save'),
          onClick: isLoading ? undefined : handleSave,
          variant: 'accent',
          disabled: !isFormValid,
          loading: isLoading,
        },
        {
          children: t('Modal.Ometre'),
          onClick: onClose,
          variant: 'tertiary',
        },
      ]}
      isOpen={show}
      onClose={onClose}
      title={t('classroom.title.assignStudents')}
    >
      <ModalContent>
        <StudentsSection>
          <Typography.Body2>
            {t('classroom.students.description')}
          </Typography.Body2>
          {(failures?.length ?? 0) > 0 && (
            <AnnouncementPanel
              type="error"
              text={t(
                'classroom.students.failures',
                'No hemos podido añadir los siguientes emails por que los estudiantes no existen o pertenecen a otro centro'
              )}
            />
          )}
          <InputChips
            name="students"
            onInputChange={setSearch}
            options={options}
            suggestOptions
            loadingOptions={isLoadingStudents}
            canPasteEmails
            isChipInvalid={handleIsChipInvalid}
            onUpdateChips={handleUpdateMails}
            defaultValue={selectedStudents}
          />
        </StudentsSection>
      </ModalContent>
    </Modal>
  );
}

export { AddStudentsToClassroomModal };
